import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Select } from 'formik-antd';
import { Col, Row } from 'antd';
import { FormikProps, useFormikContext } from 'formik';
import { isNil, isString } from 'lodash';

import { getFormItemName } from 'utils/fieldArrayUtils';
import FormItem from 'components/formik/FormItem';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  FinancialInfoFormValues,
  MultiRateFormValues,
  PayCodeInfoFormValues,
} from 'components/modals/AssignmentDataModal/FinancialInfo';
import {
  payRateTypesSelector,
  payCodesSelector,
} from 'redux/selectors/assignmentSelectors';
import { AssignmentTypeEnum, PayRateTypeModel } from 'api/backofficeApiClient';
import PayCodeDetails from './PayCodeDetails';
import Radio from 'components/formik/Radio';
import {
  FinancialFormValues,
  MULTI_RATES_KEY,
} from 'hooks/useFinancialInfoTabs';
import {
  FinancialSchedulerFormValues,
  PayCodeInfoFormScheduleValues,
} from 'components/modals/FinancialInfoScheduleModal';
import {
  NOT_AVAILABLE_PAY_CODE,
  ptoMultiRateId,
} from 'constants/MultiRateConstants';
import FinancialSchedulerContext from 'components/FinancialSchedulerContext';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { filterPayTypes } from 'utils/multiRates/multiRatesUtils';
import { getPayCodes } from 'features/assignment/AssignmentDetailsPage/utils';
import { FinancialInfoCalculator } from '../calculationUtils';

import styles from '../FinancialInfo.module.less';

type MultiRateDetailsProps = {
  index: number;
  formNamePrefix: string;
  updatePayType: (
    payTypeId: string,
    index: number,
    formik: FormikProps<FinancialFormValues>,
  ) => void;
  isScheduleMode: boolean;
  assignmentType: AssignmentTypeEnum | null;
  payTypeId: string;
};

export default function MultiRateDetails(props: MultiRateDetailsProps) {
  const {
    formNamePrefix,
    index: payTypeIndex,
    updatePayType,
    isScheduleMode,
    assignmentType,
    payTypeId,
  } = props;
  const payRateTypes = useAppSelector(payRateTypesSelector);
  const payCodesDetails = useAppSelector(payCodesSelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const [filteredPayTypes, setFilteredPayTypes] = useState<PayRateTypeModel[]>(
    [],
  );
  const formik: FormikProps<FinancialFormValues> =
    useFormikContext<FinancialFormValues>();
  const { predefinedFinancialSchedulerValues } = useContext(
    FinancialSchedulerContext,
  );
  const isCanUpdateScheduler = (formik.values as FinancialSchedulerFormValues)
    ?.isCanUpdateScheduler;

  const scheduledMultiRateChange = useMemo(() => {
    return predefinedFinancialSchedulerValues?.multiRates?.find(
      (multiRate) => multiRate.payTypeId === payTypeId,
    );
  }, [predefinedFinancialSchedulerValues?.multiRates, payTypeId]);

  const hasScheduledMultiRateChange = Boolean(scheduledMultiRateChange);

  const payCodes = getPayCodes(payCodesDetails, assignmentType);

  const payCodeInfoErrorMsg = useMemo(() => {
    const payCodeInfoErrorMsg = (
      formik.errors.multiRates?.[payTypeIndex] as any
    )?.payCodesInfo;

    const hasError = isString(payCodeInfoErrorMsg);

    const hasTouched = Boolean(
      (formik.touched.multiRates as any)?.[payTypeIndex]?.payCodesInfo,
    );

    return hasError && hasTouched ? payCodeInfoErrorMsg : undefined;
  }, [formik.errors.multiRates, formik.touched.multiRates, payTypeIndex]);

  const multiRatesValues: MultiRateFormValues[] = useMemo(
    () => formik.values.multiRates || [],
    [formik.values.multiRates],
  );

  const payRateTypeFormItemName = getFormItemName({
    name: 'payTypeId',
    index: payTypeIndex,
    prefix: formNamePrefix,
  });

  const payCodeFormItemName = getFormItemName({
    name: 'activePayCodeId',
    index: payTypeIndex,
    prefix: formNamePrefix,
  });

  const payCodeItems = useMemo(() => {
    const multiRateValue = (multiRatesValues || [])[payTypeIndex];

    return payCodes.filter(
      (payCode) => payCode.payRateTypeId === multiRateValue?.payTypeId,
    );
  }, [payTypeIndex, payCodes, multiRatesValues]);

  const getPayCodeItemById = useCallback(
    (payCodeId?: string) => {
      return payCodeItems.find((payCode) => payCode.id === payCodeId);
    },
    [payCodeItems],
  );

  const currentRateTypeInfo = useMemo(() => {
    const currentPayTypeId =
      formik.values.multiRates?.[payTypeIndex]?.payTypeId;

    return payRateTypes.find(
      (payRateType) => payRateType.id === currentPayTypeId,
    );
    // eslint-disable-next-line
  }, [formik.values.multiRates?.[payTypeIndex]?.payTypeId, payRateTypes]);

  const checkIsPayRateDisable = (
    scheduledPayCodeInfo?: PayCodeInfoFormScheduleValues,
  ) =>
    isCanUpdateScheduler
      ? false
      : scheduledPayCodeInfo?.hasOwnProperty('payRate');

  const checkIsBillRateDisable = (
    scheduledPayCodeInfo?: PayCodeInfoFormScheduleValues,
  ) =>
    isCanUpdateScheduler
      ? false
      : scheduledPayCodeInfo?.hasOwnProperty('billRate');

  const getFilteredPayTypes = useCallback(() => {
    const usedPayTypeIds = multiRatesValues.map((rate) => rate.payTypeId);
    const currentPayTypeId = multiRatesValues[payTypeIndex]?.payTypeId;

    return filterPayTypes({
      payRateTypes,
      payCodes,
      usedPayTypeIds,
      currentPayTypeId,
    });
  }, [multiRatesValues, payRateTypes, payTypeIndex, payCodes]);

  const getScheduledPayCodeInfo = (payCodeId?: string) => {
    return (scheduledMultiRateChange?.payCodesInfo || []).find(
      (scheduledPayCode) => scheduledPayCode.payCodeId === payCodeId,
    );
  };

  const handlePayTypeChange = (payTypeId: string, index: number) => {
    formik.setFieldValue(payRateTypeFormItemName, payTypeId);
    updatePayType(payTypeId, index, formik);
  };

  const payRateFinancialInfoValue = (formik.values as FinancialInfoFormValues)
    ?.payRate;

  const onPayCodeFieldsClear = (
    payCodeFieldName: string,
    payCodeId?: string,
  ) => {
    const currentPayCodeItem = getPayCodeItemById(payCodeId);

    const initialValue: PayCodeInfoFormValues = {
      payCodeId: payCodeId,
      hasPayRateCustomLogic: false,
      hasBillRateCustomLogic: false,
      memberBilledIndicator:
        currentPayCodeItem?.memberBilledIndicatorDefaultValue,
      specialInstructions: undefined,
    };

    const isPTOPayType = currentPayCodeItem?.payRateTypeId === ptoMultiRateId;

    //https://github.com/s-yadav/react-number-format/issues/528
    initialValue.payRate = isPTOPayType
      ? FinancialInfoCalculator.calculatePayRatePTOValue(
          payRateFinancialInfoValue,
        )
      : ('' as any);
    initialValue.billRate = '' as any;

    formik.setFieldValue(payCodeFieldName, initialValue);
    formik.setFieldTouched(payCodeFieldName, true, false);
  };

  useEffect(() => {
    const filtered = getFilteredPayTypes();
    setFilteredPayTypes(filtered);
    // eslint-disable-next-line
  }, []);

  const getAvailableListOfPayCode = () => {
    return filterPayCodes().filter(
      (payCode) => payCode.name !== NOT_AVAILABLE_PAY_CODE,
    );
  };

  const filterPayCodes = () => {
    return payCodeItems.filter(
      (payCode) =>
        multiRatesValues[payTypeIndex]?.payCodesInfo?.some(
          (payCodeInfoFormValue) =>
            payCodeInfoFormValue.payCodeId === payCode.id,
        ),
    );
  };

  const listOfPayCodes = getAvailableListOfPayCode();
  const filteredPayCodes = filterPayCodes();

  return (
    <Row gutter={40}>
      <Col xs={24} md={12}>
        <FormItem name={payRateTypeFormItemName} label="Pay rate type *">
          <Select
            name={payRateTypeFormItemName}
            placeholder="Select rate type"
            onChange={(value) => handlePayTypeChange(value, payTypeIndex)}
            disabled={isScheduleMode || hasScheduledMultiRateChange}
          >
            {filteredPayTypes.map((payRateTypeInfo) => (
              <Select.Option
                key={`select-payRateType-${payRateTypeInfo.id}`}
                value={payRateTypeInfo.id}
              >
                <div className={styles.selectTitle}>
                  {payRateTypeInfo?.name}
                  {!isNil(payRateTypeInfo?.title) && (
                    <span> - {payRateTypeInfo?.title}</span>
                  )}
                </div>
                <div className={styles.selectText}>
                  {payRateTypeInfo?.description}
                </div>
              </Select.Option>
            ))}
          </Select>
        </FormItem>
      </Col>
      <Col xs={24} md={12} />
      <Col xs={24} md={24}>
        {!isNil(currentRateTypeInfo?.additionalDescription) &&
          !isScheduleMode && (
            <div className={styles.hint}>
              <i className="icon icon-info-filled" />
              {currentRateTypeInfo?.additionalDescription}
            </div>
          )}
      </Col>

      {!!payCodeItems?.length && (
        <>
          {listOfPayCodes.length > 0 && (
            <Col xs={24}>
              <div className={styles.codeTitle}>Pay Code(s)</div>
              {!isNil(payCodeInfoErrorMsg) && (
                <h5 className={styles.errorText}>{payCodeInfoErrorMsg}</h5>
              )}
              <div className={styles.group}>
                <FormItem name={payCodeFormItemName}>
                  <Radio.Group
                    name={payCodeFormItemName}
                    className="radio-tab-view"
                  >
                    {listOfPayCodes.map((payCode) => (
                      <Radio.Button key={payCode.id} value={payCode.id}>
                        {payCode.name}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </FormItem>
              </div>
            </Col>
          )}

          <Col xs={24} md={24}>
            {filteredPayCodes.map((payCode, payCodeIndex) => {
              if (multiRatesValues[payTypeIndex].activePayCodeId !== payCode.id)
                return null;

              const scheduledPayCodeInfo = getScheduledPayCodeInfo(payCode.id);

              return (
                <PayCodeDetails
                  financialInfoForm={formik}
                  key={payCode.id}
                  payCodeIndex={payCodeIndex}
                  payCodeDescription={payCode.description}
                  formNamePrefix={`${MULTI_RATES_KEY}[${payTypeIndex}].payCodesInfo`}
                  payCodeForm={
                    multiRatesValues[payTypeIndex].payCodesInfo?.[payCodeIndex]
                  }
                  scheduledPayCodeInfo={scheduledPayCodeInfo}
                  isScheduleMode={isScheduleMode}
                  isCanUpdateScheduler={isCanUpdateScheduler}
                  isAdmin={isAdmin}
                  currentRateTypeInfo={currentRateTypeInfo}
                  onPayCodeFieldsClear={onPayCodeFieldsClear}
                  checkIsPayRateDisable={checkIsPayRateDisable}
                  checkIsBillRateDisable={checkIsBillRateDisable}
                  getPayCodeItemById={getPayCodeItemById}
                  payRateFinancialInfoValue={payRateFinancialInfoValue}
                />
              );
            })}
          </Col>
        </>
      )}
    </Row>
  );
}
